/**
 * Helpers for customer shipment `shipment_unit` (nested object on read-only customer APIs).
 * Supports legacy `shipment_unit_serial_number` during rollout.
 */

import { useMemo } from 'react';
import { useSiteSettings } from '../../main/context/SiteSettingsContext';

export const CUSTOMER_SHIPMENT_UNIT_FIELD_KEYS = [
  'serial_number',
  'tracking_url',
  'origin_warehouse',
  'destination_warehouse',
  'expected_destination_eta',
  'expected_warehouse_eta',
];

export function getCustomerShipmentUnitVisibleFieldsFromSettings(settings) {
  const fields = settings?.customer_shipment_unit_visible_fields;
  return Array.isArray(fields) ? fields : [];
}

export function useCustomerShipmentUnitVisibleFields() {
  const { settings } = useSiteSettings();
  return useMemo(
    () => getCustomerShipmentUnitVisibleFieldsFromSettings(settings),
    [settings]
  );
}

export function isCustomerShipmentUnitFieldVisible(fieldKey, visibleFields) {
  return Array.isArray(visibleFields) && visibleFields.includes(fieldKey);
}

export function hasAnyVisibleCustomerUnitField(visibleFields) {
  return Array.isArray(visibleFields) && visibleFields.length > 0;
}

export function getCustomerShipmentUnit(shipment) {
  if (!shipment || shipment.shipment_unit == null) return null;
  return shipment.shipment_unit;
}

export function getCustomerShipmentUnitSerial(shipment) {
  if (!shipment) return null;
  const fromUnit = shipment.shipment_unit?.serial_number;
  if (fromUnit != null && fromUnit !== '') return String(fromUnit);
  const legacy = shipment.shipment_unit_serial_number;
  if (legacy != null && legacy !== '') return String(legacy);
  return null;
}

/** Format ISO date strings (YYYY-MM-DD) for customer unit ETA fields. */
export function formatCustomerShipmentUnitDate(value) {
  if (!value) return '—';
  try {
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
      }
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (_) {
    return '—';
  }
}
